<script lang="ts">
  import type { GoalPreferences } from "@contract/types";
  import { bridge } from "../bridge/client";
  import { commitGoalChange } from "../lib/goalCorrection";
  import {
    effectiveHrReferenceBpm,
    hrZones,
    unsetGoals,
  } from "../lib/goalDefaults";
  import { haptic } from "../lib/haptics";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { closeOverlay, bumpGoalsVersion, type RingId } from "../lib/overlay";

  let {
    open = false,
    ring,
    medianWeekKm = null,
  }: {
    open?: boolean;
    ring: RingId;
    medianWeekKm?: number | null;
  } = $props();

  let prefs: GoalPreferences = $state(unsetGoals());
  let loaded = $state(false);

  // Editable form mirrors (so the slider/input pair binds to a number).
  let weekDistanceKmInput = $state<number>(10);
  let peakHrInput = $state<number>(180);
  let ageYearsInput = $state<number | null>(null);

  // Lock background scroll while the sheet is open. (inert on the app shell is
  // owned by App.svelte's reactive attribute now — App inerts the whole shell
  // including the FAB whenever any overlay is active.)
  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  });

  // (Re)load goals every time the sheet opens. Snapshot harness opens →
  // closes → opens repeatedly; refresh keeps the stub-state in sync.
  $effect(() => {
    if (!open) {
      loaded = false;
      return;
    }
    void (async () => {
      try {
        const got = await bridge.getGoals();
        prefs = got;
      } catch {
        prefs = unsetGoals();
      } finally {
        weekDistanceKmInput =
          prefs.weekDistanceMeters != null ? prefs.weekDistanceMeters / 1000 : 10;
        peakHrInput = prefs.peakHrBpm ?? 180;
        ageYearsInput = prefs.ageYears ?? null;
        loaded = true;
      }
    })();
  });

  function close() {
    // No open=false (prop is plain now) and no ghost-click swallow: dismissing
    // clears the single overlay slot, which makes App.svelte re-inert the FAB.
    // The synthesized trailing click from this same tap lands on the now-inert
    // FAB and is a no-op — the bug is fixed by construction, not by timing.
    closeOverlay("ring");
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  // Durable, optimistic save (mirrors lap-label commitLabelChange): the edit is
  // journaled BEFORE the bridge write and persisted with read-back verification
  // in the background, so we close the sheet immediately. A stranded or failed
  // save survives sheet close / app background / relaunch and surfaces through
  // the shared save-trouble pill — not a sheet-only inline error that vanishes
  // the moment the sheet closes.
  function saveWeekDistance() {
    const next: GoalPreferences = {
      ...prefs,
      weekDistanceMeters: Math.round(weekDistanceKmInput * 1000),
    };
    commitGoalChange(next);
    haptic("success");
    bumpGoalsVersion();
    close();
  }

  function savePeakHr() {
    const next: GoalPreferences = {
      ...prefs,
      peakHrBpm: Math.round(peakHrInput),
      ageYears: ageYearsInput,
    };
    commitGoalChange(next);
    haptic("success");
    bumpGoalsVersion();
    close();
  }

  function fillFromAge() {
    if (ageYearsInput != null) {
      peakHrInput = 220 - ageYearsInput;
      haptic("selection");
    }
  }

  // Live-derived zones for the Peak HR sheet body. Uses the in-form input,
  // not the persisted prefs, so the table updates while editing.
  let liveZones = $derived(
    hrZones(
      effectiveHrReferenceBpm({
        ...prefs,
        peakHrBpm: peakHrInput,
        ageYears: ageYearsInput,
      }),
    ),
  );

  const titles: Record<RingId, string> = {
    "week-distance": "Weekly distance target",
    "peak-hr": "Peak heart rate target",
    "week-streak": "Weeks swum",
    "day-streak": "Day streak",
  };
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div
    class="scrim"
    role="presentation"
    onclick={close}
    onkeydown={(e) => e.key === "Enter" && close()}
  ></div>
  <div
    class="sheet"
    role="dialog"
    aria-modal="true"
    aria-label={titles[ring]}
    use:sheetDrag={close}
  >
    <div class="grabber" aria-hidden="true"></div>
    <h2 class="title">{titles[ring]}</h2>

    {#if ring === "week-distance"}
      <div class="body">
        <label class="field">
          <span class="field-label">Target</span>
          <div class="num-row">
            <input
              type="number"
              min="0"
              max="50"
              step="0.5"
              bind:value={weekDistanceKmInput}
              aria-label="Weekly distance target in kilometers"
            />
            <span class="unit">km / week</span>
            <span class="unit-echo">= {Math.round((weekDistanceKmInput || 0) * 1000)} m</span>
          </div>
          <input
            type="range"
            min="0"
            max="50"
            step="0.5"
            bind:value={weekDistanceKmInput}
            aria-label="Weekly distance target slider"
          />
        </label>

        <p class="suggestion">
          {#if medianWeekKm != null}
            Your typical week so far: {medianWeekKm.toFixed(1)} km
          {:else}
            Not enough swims logged yet to suggest a target.
          {/if}
        </p>
      </div>
      <div class="footer">
        <button type="button" class="btn ghost" onclick={close}>
          Cancel
        </button>
        <button
          type="button"
          class="btn primary"
          disabled={!loaded}
          onclick={saveWeekDistance}
        >
          Save
        </button>
      </div>
    {:else if ring === "peak-hr"}
      <div class="body">
        <label class="field">
          <span class="field-label">Max HR</span>
          <div class="num-row">
            <input
              type="number"
              min="60"
              max="230"
              step="1"
              bind:value={peakHrInput}
              aria-label="Max heart rate in bpm"
            />
            <span class="unit">bpm</span>
          </div>
        </label>

        <label class="field">
          <span class="field-label">Age</span>
          <div class="num-row">
            <input
              type="number"
              min="5"
              max="120"
              step="1"
              bind:value={ageYearsInput}
              aria-label="Age in years"
            />
            <span class="unit">years</span>
            <button
              type="button"
              class="inline-btn"
              disabled={ageYearsInput == null}
              onclick={fillFromAge}
            >
              220 − age
            </button>
          </div>
        </label>

        <div class="zones" aria-label="Heart rate zones">
          {#each liveZones as z (z.label)}
            <div class="zone-row">
              <span class="zone-label">{z.label}</span>
              <span class="zone-range">{z.lo}–{z.hi} bpm</span>
            </div>
          {/each}
        </div>
      </div>
      <div class="footer">
        <button type="button" class="btn ghost" onclick={close}>
          Cancel
        </button>
        <button
          type="button"
          class="btn primary"
          disabled={!loaded}
          onclick={savePeakHr}
        >
          Save
        </button>
      </div>
    {:else if ring === "week-streak"}
      <div class="body">
        <p class="explainer">
          Cumulative weeks-this-year, not a consecutive streak. Skipping a
          week pulls the count down by about 1/N — it never resets to zero.
        </p>
        <p class="explainer muted">
          Not goal-able: streak panic is the wrong incentive for swimming.
        </p>
      </div>
      <div class="footer">
        <button type="button" class="btn primary" onclick={close}>
          Got it
        </button>
      </div>
    {:else if ring === "day-streak"}
      <div class="body">
        <p class="explainer">
          Days in a row with a recorded swim. Shown for awareness, not as a
          goal.
        </p>
        <p class="explainer muted">
          Loss-aversion mechanics on streaks were rejected in design review —
          we don't want you swimming hurt to keep a number.
        </p>
      </div>
      <div class="footer">
        <button type="button" class="btn primary" onclick={close}>
          Got it
        </button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    /* Hard-stop scroll + zoom that begins on the scrim. The body scroll-lock
       handles the page-level case; this prevents touch sequences from
       passing through to anything behind the modal. */
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    /* Opaque modal: the global Dawn-Glass --bg-card is 42% opaque and this
       sheet gets no backdrop blur (it's a dialog, not a .card), so the fixed
       BottomNav ("Swim / Sets / Review") ghosted through the footer and made
       Cancel vs Save ambiguous. Restore the base opaque card surface for THIS
       modal only by re-scoping --bg-card locally — the global token is
       untouched, so every Dawn-Glass content card stays translucent. */
    --bg-card: var(--gray-1);
    background: var(--bg-card);
    border-top: 1px solid var(--divider);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    padding: var(--space-3) var(--space-4)
      calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    box-shadow: var(--shadow-floating);
    animation: sheet-in var(--dur-card) var(--ease-swim);
    max-height: 85vh;
    overflow-y: auto;
  }
  .grabber {
    width: 36px;
    height: 4px;
    border-radius: 999px;
    background: var(--divider-strong);
    margin: 0 auto var(--space-3);
  }
  .title {
    font: var(--type-title-subtitle);
    color: var(--text-primary);
    margin: 0 0 var(--space-3) 0;
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .field-label {
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .num-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .num-row input[type="number"] {
    flex: 0 0 96px;
    padding: var(--space-2) var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--text-primary);
    font: var(--type-title-subtitle);
    font-variant-numeric: tabular-nums;
  }
  .num-row input[type="number"]:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 1px;
  }
  .unit {
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  .unit-echo {
    font-size: 14px;
    color: var(--text-muted);
    margin-left: 0.4rem;
  }
  input[type="range"] {
    width: 100%;
    accent-color: var(--accent-primary);
  }
  .inline-btn {
    margin-left: auto;
    padding: var(--space-1, 4px) var(--space-2);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--accent-primary);
    font: var(--type-caption);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .inline-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .suggestion {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  .zones {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: var(--space-2);
  }
  .zone-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: var(--space-1, 4px) var(--space-2);
    font: var(--type-body-sm);
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }
  .zone-label {
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.04em;
  }
  .zone-range {
    color: var(--text-primary);
  }
  .explainer {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-primary);
    line-height: 1.45;
  }
  .explainer.muted {
    color: var(--text-muted);
  }
  .footer {
    display: flex;
    gap: var(--space-2);
    margin-top: var(--space-4);
    justify-content: flex-end;
  }
  .btn {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-button);
    border: 1px solid var(--divider);
    background: var(--bg-surface);
    color: var(--text-primary);
    font: var(--type-title-subtitle);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .btn.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: var(--text-on-accent);
    font-weight: 700;
  }
  .btn.ghost {
    background: transparent;
  }
  .btn:disabled {
    opacity: 1;
    background: var(--bg-raised);
    border-color: var(--divider);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
</style>
